// API Service for Green Door CRM
import axios from 'axios';
import { cacheApiResponse, getCachedApiResponse } from './offlineDB';

// Always use the current page's origin so the bundle works correctly in any
// deployment environment (preview, staging, production, custom domain) without
// needing a URL baked in at build time.
export const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token and user timezone to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  try {
    config.headers['X-Timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch { /* ignore — old browsers */ }
  return config;
});

// Cache successful GET responses; serve from cache when offline
api.interceptors.response.use(
  (response) => {
    // Cache all successful GET responses (skip blobs and auth endpoints)
    if (
      response.config.method === 'get' &&
      response.status === 200 &&
      response.config.responseType !== 'blob' &&
      !response.config.url?.includes('/auth/')
    ) {
      const cacheKey = response.config.url + (response.config.params
        ? '?' + new URLSearchParams(response.config.params).toString()
        : '');
      cacheApiResponse(cacheKey, response.data).catch(() => {});
    }
    return response;
  },
  async (error) => {
    // Handle 401 auth errors
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // For blob responses with errors, return a clean error
    if (error.config?.responseType === 'blob' && error.response) {
      const cleanError = new Error(`Request failed with status ${error.response.status}`);
      cleanError.response = { status: error.response.status };
      cleanError.config = error.config;
      return Promise.reject(cleanError);
    }

    // Network error on a GET — try the offline cache
    const isNetworkError = !error.response;
    const isGet = error.config?.method === 'get';
    if (isNetworkError && isGet && error.config?.url) {
      const cacheKey = error.config.url + (error.config.params
        ? '?' + new URLSearchParams(error.config.params).toString()
        : '');
      const cached = await getCachedApiResponse(cacheKey);
      if (cached !== null) {
        // Return a synthetic response that looks like a real axios response
        return Promise.resolve({
          data: cached,
          status: 200,
          statusText: 'OK (cached)',
          headers: { 'x-served-from-cache': 'true' },
          config: error.config,
          fromCache: true,
        });
      }
    }

    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  ownerRegister: (data) => api.post('/auth/owner-register', data),
  getMe: () => api.get('/auth/me'),
};

// Tenant APIs
export const tenantAPI = {
  list: () => api.get('/tenants'),
  get: (id) => api.get(`/tenants/${id}`),
  create: (data) => api.post('/tenants', data),
  update: (id, data) => api.put(`/tenants/${id}`, data),
  suspend: (id) => api.post(`/tenants/${id}/suspend`),
  activate: (id) => api.post(`/tenants/${id}/activate`),
  delete: (id) => api.delete(`/tenants/${id}`),
  getMyBranding: () => api.get('/tenants/my-branding'),
};

// User APIs
export const userAPI = {
  list: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
};

// CRM Entity APIs
export const crmAPI = {
  list: (params) => api.get('/crm-entities', { params }),
  get: (id) => api.get(`/crm-entities/${id}`),
  create: (data) => api.post('/crm-entities', data),
  update: (id, data) => api.put(`/crm-entities/${id}`, data),
  delete: (id) => api.delete(`/crm-entities/${id}`),
};
// Association APIs
export const associationAPI = {
  list: (entityId) => api.get(`/crm-entities/${entityId}/associations`),
  create: (data) => api.post('/associations', data),
  update: (id, data) => api.put(`/associations/${id}`, data),
  delete: (id) => api.delete(`/associations/${id}`),
};


// Horse APIs
export const horseAPI = {
  list: (params = {}) => api.get('/horses', { params }),
  get: (id) => api.get(`/horses/${id}`),
  create: (data) => api.post('/horses', data),
  update: (id, data) => api.put(`/horses/${id}`, data),
  getOwnerships: (id) => api.get(`/horses/${id}/ownerships`),
};

// Ownership APIs
export const ownershipAPI = {
  create: (data) => api.post('/ownerships', data),
};

// Sale APIs
export const saleAPI = {
  list: () => api.get('/sales'),
  get: (id) => api.get(`/sales/${id}`),
  create: (data) => api.post('/sales', data),
  getSessions: (id) => api.get(`/sales/${id}/sessions`),
};

// Sale Session APIs
export const saleSessionAPI = {
  create: (data) => api.post('/sale-sessions', data),
};

// Sale Opportunity APIs
export const saleOpportunityAPI = {
  list: (params) => api.get('/sale-opportunities', { params }),
  get: (id) => api.get(`/sale-opportunities/${id}`),
  create: (data) => api.post('/sale-opportunities', data),
  update: (id, data) => api.put(`/sale-opportunities/${id}`, data),
  delete: (id) => api.delete(`/sale-opportunities/${id}`),
};

// Sale Card APIs
export const saleCardAPI = {
  list: (saleId) => api.get('/sale-cards', { params: { sale_id: saleId } }),
  create: (data) => api.post('/sale-cards', data),
};

// Invoice APIs
export const invoiceAPI = {
  list: () => api.get('/invoices'),
  get: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
};

// Activity APIs
export const activityAPI = {
  list: (params) => api.get('/activities', { params }),
  create: (data) => api.post('/activities', data),
  update: (id, data) => api.put(`/activities/${id}`, data),
  delete: (id) => api.delete(`/activities/${id}`),
  getCalendarEvents: (startDate, endDate) => 
    api.get('/activities/calendar', { params: { start_date: startDate, end_date: endDate } }),
  getMyTasks: () => api.get('/activities/tasks/my'),
  getDepartmentTasks: () => api.get('/activities/tasks/department'),
};

// Hospitality & Gifts APIs
export const hospitalityAPI = {
  list: (params) => api.get('/hospitality', { params }),
  create: (data) => api.post('/hospitality', data),
  update: (id, data) => api.put(`/hospitality/${id}`, data),
  delete: (id) => api.delete(`/hospitality/${id}`),
};

// Marketing Campaign APIs
export const marketingAPI = {
  list: (params) => api.get('/marketing', { params }),
  create: (data) => api.post('/marketing', data),
  update: (id, data) => api.put(`/marketing/${id}`, data),
  delete: (id) => api.delete(`/marketing/${id}`),
};

// Dashboard APIs
export const dashboardAPI = {
  getTenantDashboard: () => api.get('/dashboard/tenant'),
  getSystemDashboard: () => api.get('/dashboard/system'),
};

// Owner Portal APIs
export const ownerPortalAPI = {
  getDashboard: () => api.get('/owner-portal/dashboard'),
};

// Health check
export const healthCheck = () => api.get('/health');

export default api;
